import type { AddBuildingOp, RawDataGroup, RemoveBuildingOp } from '@visualizer/types';
import { BuildingSource, PreProcessingTypes } from '@visualizer/types';
import type { BuildingsComposite } from '@data/geodata/building/BuildingsComposite';
import FVector from '@data/geometry/FVector';
import { isPointInPolygon } from '@data/geometry/Functions';
import Grid from 'Geodata/grid';
import type { GeoEditManager } from './GeoEditManager';

type SelectedBuilding = {
    source: BuildingSource;
    id: number;
};

export function convertBuildingsToProjectSketch(args: {
    editManager: GeoEditManager;
    buildings: BuildingsComposite;
    selection: SelectedBuilding[];
}): number {
    const { editManager, buildings, selection } = args;
    const constrainPolygon = getBoundingBoxPolygon2D();

    let converted = 0;

    for (const item of selection) {
        if (item.source === BuildingSource.SKETCH) {
            continue;
        }

        const newId = editManager.allocateTempId();
        const groups = buildings.getRawGroupsForBuilding(item.source, item.id, {
            source: BuildingSource.SKETCH,
            id: newId,
        });

        if (!groups || groups.length === 0) {
            continue;
        }

        if (constrainPolygon.length >= 3 && !isBuildingInsidePolygon(groups, constrainPolygon)) {
            continue;
        }

        const removeOp: RemoveBuildingOp = {
            command: 'REMOVE',
            dataType: 'building',
            source: item.source,
            id: item.id,
        };

        const addOp: AddBuildingOp = {
            command: 'ADD',
            dataType: 'building',
            source: BuildingSource.SKETCH,
            id: newId,
            groups,
        };

        editManager.apply(removeOp);
        editManager.apply(addOp);
        converted += 1;
    }

    return converted;
}

function isBuildingInsidePolygon(groups: RawDataGroup[], polygon: FVector[]): boolean {
    const floorGroups = groups.filter(
        (group) =>
            group.type === PreProcessingTypes.SWISSTOPO_FLOOR ||
            group.type === PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR ||
            group.type === PreProcessingTypes.PROJECT_SKETCH_FLOOR
    );
    const groupsToCheck = floorGroups.length > 0 ? floorGroups : groups;

    for (const group of groupsToCheck) {
        for (const obj of group.objects ?? []) {
            for (const point of obj.points ?? []) {
                if (!isPointInPolygon(new FVector(point.x, point.y, point.z), polygon)) {
                    return false;
                }
            }
        }
    }

    return true;
}

function getBoundingBoxPolygon2D(): FVector[] {
    const unique = new Map<string, FVector>();
    for (const p of Grid.boundingBox ?? []) {
        if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
        const key = `${p.x}:${p.y}`;
        if (!unique.has(key)) {
            unique.set(key, new FVector(p.x, p.y, p.z ?? 0));
        }
    }

    return [...unique.values()];
}
